import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, LifeBuoy, RefreshCw, Send, ShieldCheck } from "lucide-react";
import { getStaffSupportCase, listStaffSupportCases, replyFromSupportStaff, updateSupportCaseStatus, type SupportCase, type SupportStatus } from "../api/support";

export default function SupportQueue({ active, canReply, canChangeStatus }: {
  active: boolean;
  canReply: boolean;
  canChangeStatus: boolean;
}) {
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState<SupportStatus | "all">("open");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<SupportCase | null>(null);
  const [mobileListOpen, setMobileListOpen] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const streamRef = useRef<HTMLDivElement>(null);
  const selectedIdRef = useRef<string | null>(null);
  const pageRef = useRef(1);
  const loadGenerationRef = useRef(0);
  selectedIdRef.current = selectedId;

  const load = useCallback(async () => {
    if (!active) return;
    const generation = ++loadGenerationRef.current;
    setLoading(true);
    try {
      const result = await listStaffSupportCases(filter, 1);
      if (generation !== loadGenerationRef.current) return;
      setCases((current) => pageRef.current > 1
        ? [...result.cases, ...current.filter((item) => (filter === "all" || item.status === filter) && !result.cases.some((latest) => latest.id === item.id))]
        : result.cases);
      setHasMore(result.hasMore);
      setSelectedId((current) => current || result.cases[0]?.id || null);
      setError("");
    } catch (failure) {
      if (generation === loadGenerationRef.current) setError(failure instanceof Error ? failure.message : "Could not load support cases.");
    } finally {
      if (generation === loadGenerationRef.current) setLoading(false);
    }
  }, [active, filter]);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    const generation = loadGenerationRef.current;
    setLoadingMore(true);
    try {
      const result = await listStaffSupportCases(filter, page + 1);
      if (generation !== loadGenerationRef.current) return;
      setCases((current) => [...current, ...result.cases.filter((item) => !current.some((entry) => entry.id === item.id))]);
      setPage(page + 1);
      pageRef.current = page + 1;
      setHasMore(result.hasMore);
    } catch (failure) {
      if (generation === loadGenerationRef.current) setError(failure instanceof Error ? failure.message : "Could not load more cases.");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { pageRef.current = 1; setCases([]); setPage(1); setSelectedId(null); setSelectedDetail(null); setMobileListOpen(true); }, [filter]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!active) return;
    const interval = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, 30000);
    return () => window.clearInterval(interval);
  }, [active, load]);

  useEffect(() => {
    if (!active || !selectedId) { setSelectedDetail(null); return; }
    setSelectedDetail(null);
    const refreshDetail = () => getStaffSupportCase(selectedId)
      .then((result) => { if (selectedIdRef.current === selectedId) setSelectedDetail(result.supportCase); })
      .catch((failure) => { if (selectedIdRef.current === selectedId) setError(failure instanceof Error ? failure.message : "Could not load this conversation."); });
    void refreshDetail();
    const interval = window.setInterval(() => { if (document.visibilityState === "visible") void refreshDetail(); }, 30000);
    return () => window.clearInterval(interval);
  }, [active, selectedId]);

  const selected = selectedDetail?.id === selectedId ? selectedDetail : null;
  useEffect(() => { streamRef.current?.scrollTo({ top: streamRef.current.scrollHeight }); }, [selected?.messages?.length]);

  const reply = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected || !canReply || draft.trim().length < 2 || busy) return;
    setBusy(true);
    try {
      const result = await replyFromSupportStaff(selected.id, draft.trim());
      setSelectedDetail(result.supportCase);
      setCases((current) => current.map((item) => item.id === selected.id ? { ...item, ...result.supportCase, messages: undefined } : item));
      setDraft("");
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not send reply.");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status: SupportStatus) => {
    if (!selected || !canChangeStatus || busy) return;
    setBusy(true);
    try {
      const result = await updateSupportCaseStatus(selected.id, status);
      setSelectedDetail(result.supportCase);
      setCases((current) => current.map((item) => item.id === selected.id ? { ...item, ...result.supportCase, messages: undefined } : item));
      setError("");
      if (filter !== "all" && filter !== status) {
        setSelectedId(null);
        setSelectedDetail(null);
        setMobileListOpen(true);
        setCases((current) => current.filter((item) => item.id !== selected.id));
        void load();
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not update case.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 text-white">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-cyan-300/10 bg-[#070b13] px-4 py-4">
        <span className="grid h-10 w-10 place-items-center rounded-xl border border-cyan-300/20 bg-cyan-300/[.07] text-cyan-200"><LifeBuoy size={19} /></span>
        <div className="min-w-0 flex-1"><h2 className="text-lg font-bold">Account support</h2><p className="mt-0.5 text-xs text-slate-500">Replies are delivered to the member’s inbox as Blackspace Support.</p></div>
        <button type="button" onClick={() => void load()} disabled={loading} aria-label="Refresh support cases" className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-slate-300 hover:bg-white/[.07]"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /></button>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filter support cases">
        {(["open", "resolved", "closed", "all"] as const).map((option) => <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={filter === option} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold capitalize ${filter === option ? "border-cyan-300/40 bg-cyan-300/10 text-cyan-100" : "border-white/10 text-slate-400 hover:text-white"}`}>{option}</button>)}
      </div>
      {error && <p role="alert" className="rounded-lg border border-rose-300/20 bg-rose-300/[.06] px-3 py-2 text-xs text-rose-200">{error}</p>}
      <div className="grid min-h-[28rem] gap-3 lg:grid-cols-[minmax(13rem,.75fr)_minmax(0,1.25fr)]">
        <div className={`${mobileListOpen ? "block" : "hidden"} max-h-[38rem] space-y-2 overflow-y-auto rounded-2xl border border-white/[.07] bg-[#060912] p-2 lg:block`}>
          {cases.length === 0 && <p className="px-3 py-10 text-center text-sm text-slate-500">{loading ? "Loading support cases…" : "No cases in this view."}</p>}
          {cases.map((item) => <button key={item.id} type="button" onClick={() => { setSelectedId(item.id); setMobileListOpen(false); }} className={`block w-full rounded-xl border px-3 py-3 text-left transition ${item.id === selectedId ? "border-cyan-300/35 bg-cyan-300/[.07]" : "border-white/[.05] bg-white/[.02] hover:border-white/15"}`}><span className="flex items-center justify-between gap-2"><strong className="truncate text-sm text-white">@{item.owner?.username || "member"}</strong><span className="text-[10px] capitalize text-cyan-200">{item.status}</span></span><span className="mt-1 block text-xs font-semibold text-slate-400">{item.category}</span><span className="mt-1 block truncate text-xs text-slate-500">{item.lastPreview}</span></button>)}
          {hasMore && <button type="button" onClick={() => void loadMore()} disabled={loadingMore} className="w-full rounded-lg px-3 py-2 text-xs font-semibold text-cyan-200 hover:bg-cyan-300/[.05] disabled:opacity-50">{loadingMore ? "Loading…" : "Load older cases"}</button>}
        </div>
        <div className={`${mobileListOpen ? "hidden" : "flex"} min-h-[28rem] flex-col overflow-hidden rounded-2xl border border-white/[.07] bg-[#060912] lg:flex`}>
          {selected ? <>
            <header className="flex items-center gap-3 border-b border-white/[.07] px-4 py-3"><button type="button" onClick={() => setMobileListOpen(true)} aria-label="Back to support cases" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/10 text-slate-300 lg:hidden"><ArrowLeft size={17} /></button><ShieldCheck size={17} className="shrink-0 text-cyan-200" /><div className="min-w-0 flex-1"><strong className="text-sm">@{selected.owner?.username || "member"}</strong><span className="ml-2 text-xs text-slate-500">{selected.category} · {selected.status}</span></div></header>
            <div ref={streamRef} className="max-h-[28rem] min-h-56 flex-1 space-y-3 overflow-y-auto p-4">
              {selected.messages?.map((entry) => <div key={entry.id} className={`flex ${entry.author === "blackspace" ? "justify-end" : "justify-start"}`}><div className={`max-w-[85%] rounded-xl border px-3 py-2 ${entry.author === "blackspace" ? "border-cyan-300/20 bg-cyan-300/[.07]" : "border-white/10 bg-white/[.03]"}`}><p className="text-[10px] font-bold text-slate-400">{entry.author === "blackspace" ? "Blackspace · staff" : `@${selected.owner?.username || "member"}`}</p><p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-100">{entry.body}</p><p className="mt-1 text-right text-[10px] text-slate-500">{new Date(entry.createdAt).toLocaleString()}</p></div></div>)}
            </div>
            <div className="space-y-3 border-t border-white/[.07] p-4">
              {canChangeStatus && <div className="flex flex-wrap gap-2">{(["open", "resolved", "closed"] as const).map((status) => <button key={status} type="button" disabled={busy || selected.status === status} onClick={() => void changeStatus(status)} className="rounded-lg border border-white/10 px-3 py-1.5 text-xs capitalize text-slate-300 hover:border-cyan-300/30 disabled:opacity-40">{status}</button>)}</div>}
              {canReply && <form onSubmit={reply} className="flex items-end gap-2"><label className="min-w-0 flex-1"><span className="sr-only">Reply as Blackspace</span><textarea value={draft} onChange={(event) => setDraft(event.target.value.slice(0, 3000))} disabled={busy || selected.status === "closed"} rows={2} maxLength={3000} className="min-h-11 w-full resize-y rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-cyan-300/40" /></label><button type="submit" disabled={busy || selected.status === "closed" || draft.trim().length < 2} aria-label="Reply as Blackspace" className="grid h-11 w-11 place-items-center rounded-lg border border-cyan-300/30 bg-cyan-300/10 text-cyan-100 disabled:opacity-40"><Send size={17} /></button></form>}
            </div>
          </> : <div className="grid flex-1 place-items-center text-sm text-slate-500">{selectedId ? "Loading conversation…" : "Select a case to read it."}</div>}
        </div>
      </div>
    </section>
  );
}
